import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { useCompany } from '@/company/context';
import { useNamespaceCopy } from '@/i18n/ns';
import copyCatalog from '@/i18n/ns/app-function-write-setup';
import { useSessionBoundFetch } from '@/lib/use-session-guard';
import type { DeclaredCollection, WriteKind, WritingFunction } from '../control-selection-api';
import {
  createFunctionSetupApi,
  type FunctionSetupApi,
  type FunctionSetupRequest,
} from '../function-setup-api';
import { readRetained, retainedKey, writeRetained } from './retained-original';

type Props = {
  artifactId: string;
  versionId: string;
  revisionId: string;
  functions: readonly WritingFunction[];
  collections: readonly DeclaredCollection[];
  api?: FunctionSetupApi;
};

// What is retained for this person, company and selected version: the whole original request and whether it is
// unresolved or already allowed. Never authority; every replay is the same request to the same native route.
type Original = { request: FunctionSetupRequest; state: 'pending' | 'unknown' | 'allowed' };
const text = (value: unknown): value is string => typeof value === 'string' && value.length > 0;
const isOriginal = (value: unknown): value is Original => {
  const original = value as Original;
  const request = original?.request;
  return (
    ['pending', 'unknown', 'allowed'].includes(original?.state) &&
    Boolean(request) &&
    [
      request.artifactId,
      request.versionId,
      request.revisionId,
      request.functionId,
      request.operationId,
    ].every(text) &&
    ['owner-only', 'app-members'].includes(request.actorScope) &&
    Array.isArray(request.collectionEffects) &&
    request.collectionEffects.length > 0 &&
    request.collectionEffects.every(
      (effect) =>
        text(effect.collectionId) &&
        Array.isArray(effect.kinds) &&
        effect.kinds.length > 0 &&
        effect.kinds.every((kind) => ['insert', 'update', 'delete'].includes(kind)),
    )
  );
};

type Notice = null | 'denied' | 'changed' | 'invalid' | 'storage';

/**
 * The owner's explicit setup of what ONE declared function may change in the App's saved data. Every choice is
 * the person's own: nothing is preselected, so nothing is granted by pressing Allow on a default. The request names
 * the exact selected version and revision and keeps ONE original (request + operation key) for this signed-in
 * person, company and selected version until the server answers it, across a closed panel or a reload: an unknown
 * outcome is checked with that SAME request, never a successor, and an allowed one is not granted again until the
 * person explicitly chooses to set up another. Reading and opening change nothing.
 */
export function FunctionWriteSetup(props: Props) {
  const company = useCompany();
  // The coherent signed-in person and company: ready, and the membership belongs to that company.
  const member = company.data?.membership;
  const companyId = company.data?.company?.id;
  if (
    !company.ready ||
    !member?.userId ||
    !companyId ||
    member.organizationId !== companyId ||
    props.functions.length === 0 ||
    props.collections.length === 0
  )
    return null;
  const key = retainedKey('function-write', { userId: member.userId, companyId }, [
    props.artifactId,
    props.versionId,
    props.revisionId,
  ]);
  // Another person, company or selected version is another form with its own retained original.
  return <WriteForm key={key} {...props} storageKey={key} />;
}

function initial(key: string): { original: Original | null; notice: Notice } {
  try {
    return { original: readRetained(key, isOriginal), notice: null };
  } catch {
    return { original: null, notice: 'storage' };
  }
}

function WriteForm(props: Props & { storageKey: string }) {
  const copy = useNamespaceCopy(copyCatalog);
  const bound = useSessionBoundFetch();
  const [own] = useState(() => createFunctionSetupApi(bound));
  const api = props.api ?? own;
  const [view, setView] = useState(() => initial(props.storageKey));
  const [functionId, setFunctionId] = useState<string | null>(null);
  const [scope, setScope] = useState<'owner-only' | 'app-members' | null>(null);
  const [picked, setPicked] = useState<Record<string, WriteKind[]>>({});
  const [busy, setBusy] = useState(false);
  const flying = useRef(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const { original, notice } = view;
  const kindLabel: Record<WriteKind, string> = {
    insert: copy.insert,
    update: copy.update,
    delete: copy.delete,
  };
  const writing = props.functions.find((entry) => entry.id === functionId) ?? null;
  const effects = props.collections
    .filter((collection) => (picked[collection.id] ?? []).length > 0)
    .map((collection) => ({ collectionId: collection.id, kinds: picked[collection.id] }));
  const complete = Boolean(writing && scope && effects.length > 0);
  const locked = busy || original !== null || notice === 'storage';

  const run = async () => {
    if (flying.current || notice === 'storage' || original?.state === 'allowed') return;
    let current: Original;
    try {
      // Read again before sending: another tab or an earlier mount may hold the unresolved original, and it
      // is replayed as itself.
      const held = readRetained(props.storageKey, isOriginal);
      if (held?.state === 'allowed') {
        setView({ original: held, notice: null });
        return;
      }
      if (held) current = held;
      else {
        if (!writing || !scope || effects.length === 0) return;
        current = {
          state: 'pending',
          request: {
            artifactId: props.artifactId,
            versionId: props.versionId,
            revisionId: props.revisionId,
            functionId: writing.id,
            operationId: globalThis.crypto.randomUUID(),
            actorScope: scope,
            collectionEffects: effects,
          },
        };
      }
      writeRetained(props.storageKey, current);
    } catch {
      setView((now) => ({ ...now, notice: 'storage' }));
      return;
    }
    flying.current = true;
    setBusy(true);
    setView({ original: current, notice: null });
    const outcome = await api.allow(current.request);
    const next: Original | null =
      outcome.kind === 'refused'
        ? null
        : { ...current, state: outcome.kind === 'ok' ? 'allowed' : 'unknown' };
    let after: Notice =
      outcome.kind === 'refused'
        ? outcome.reason === 'not_authorized'
          ? 'denied'
          : outcome.reason
        : null;
    // The answer settles the original even if its panel was closed meanwhile; only a mounted panel renders it.
    try {
      writeRetained(props.storageKey, next);
    } catch {
      after = 'storage';
    }
    flying.current = false;
    if (!alive.current) return;
    setBusy(false);
    setView({ original: next, notice: after });
  };

  const another = () => {
    if (original?.state !== 'allowed' || flying.current) return;
    try {
      writeRetained(props.storageKey, null);
    } catch {
      setView((now) => ({ ...now, notice: 'storage' }));
      return;
    }
    setFunctionId(null);
    setScope(null);
    setPicked({});
    setView({ original: null, notice: null });
  };

  const status = notice
    ? copy[notice === 'denied' ? 'denied' : notice]
    : busy
      ? copy.checking
      : original?.state === 'allowed'
        ? copy.allowed
        : original // pending or unknown after a closed panel/reload: the outcome is not known
          ? copy.unknown
          : '';
  const toggle = (collectionId: string, kind: WriteKind) =>
    setPicked((now) => {
      const kinds = now[collectionId] ?? [];
      return {
        ...now,
        [collectionId]: kinds.includes(kind)
          ? kinds.filter((item) => item !== kind)
          : [...kinds, kind],
      };
    });
  const nameOf = (id: string) => props.functions.find((entry) => entry.id === id)?.name ?? id;
  const collectionName = (id: string) => {
    const index = props.collections.findIndex((entry) => entry.id === id);
    return props.collections[index]?.name ?? `${copy.collection} ${index + 1}`;
  };

  return (
    <section className="pc-function-write-setup" data-testid="function-write-setup">
      <h3>{copy.heading}</h3>
      <p className="pc-tracker-note">{copy.intro}</p>
      {original ? (
        // The retained original, read-only: the same request is what Check again sends.
        <dl className="pc-function-write-original" data-testid="function-write-original">
          <dt>{copy.function}</dt>
          <dd>{nameOf(original.request.functionId)}</dd>
          <dt>{copy.who}</dt>
          <dd>{original.request.actorScope === 'owner-only' ? copy.owner : copy.members}</dd>
          {original.request.collectionEffects.map((effect) => (
            <div key={effect.collectionId}>
              <dt>{collectionName(effect.collectionId)}</dt>
              <dd>
                {copy.effects}: {effect.kinds.map((kind) => kindLabel[kind]).join(', ')}
              </dd>
            </div>
          ))}
        </dl>
      ) : (
        <>
          <fieldset disabled={locked}>
            <legend>{copy.function}</legend>
            {props.functions.map((entry) => (
              <label
                className="ws-access-option"
                key={entry.id}
                data-selected={entry.id === functionId}
              >
                <input
                  type="radio"
                  name="function-write-function"
                  checked={entry.id === functionId}
                  onChange={() => {
                    setFunctionId(entry.id);
                    setPicked({});
                  }}
                />
                <span>{entry.name}</span>
              </label>
            ))}
          </fieldset>
          {writing && (
            <>
              <fieldset disabled={locked}>
                <legend>{copy.who}</legend>
                {(
                  [
                    ['owner-only', copy.owner],
                    ['app-members', copy.members],
                  ] as const
                ).map(([value, label]) => (
                  <label className="ws-access-option" key={value} data-selected={scope === value}>
                    <input
                      type="radio"
                      name="function-write-scope"
                      checked={scope === value}
                      onChange={() => setScope(value)}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </fieldset>
              <fieldset disabled={locked}>
                <legend>{copy.collection}</legend>
                {props.collections.map((collection, index) => (
                  <div key={collection.id} className="pc-function-write-collection">
                    <strong>{collection.name ?? `${copy.collection} ${index + 1}`}</strong>
                    <div>
                      {writing.writes.map((kind) => (
                        <label className="ws-access-option" key={kind}>
                          <input
                            type="checkbox"
                            checked={(picked[collection.id] ?? []).includes(kind)}
                            onChange={() => toggle(collection.id, kind)}
                          />
                          <span>{kindLabel[kind]}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </fieldset>
            </>
          )}
        </>
      )}
      {(original ? original.state !== 'allowed' : writing) && (
        <Button
          type="button"
          size="sm"
          data-testid="function-write-allow"
          disabled={busy || notice === 'storage' || (!original && !complete)}
          onClick={() => void run()}
        >
          {original && original.state !== 'allowed'
            ? copy.check
            : busy
              ? copy.checking
              : copy.allow}
        </Button>
      )}
      <p role="status" data-testid="function-write-status">
        {status}
      </p>
      {original?.state === 'allowed' && (
        <Button type="button" variant="outline" size="sm" onClick={another}>
          {copy.another}
        </Button>
      )}
    </section>
  );
}
